<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    valuePrefix: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    clickThrough: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    hasClickHandler() {
      return !!this.$.vnode.props?.onClick;
    },
    valueForCopy() {
      if (this.valuePrefix) {
        return `${this.valuePrefix}${this.value}`;
      }
      return this.value;
    },
  },
  methods: {
    handleLinkClick(e) {
      if (this.hasClickHandler) {
        e.preventDefault();
        this.$emit('click');
      } else if (!this.clickThrough) {
        e.preventDefault();
      }
    },
    async onCopy(e) {
      e.preventDefault();
      e.stopPropagation();
      await copyTextToClipboard(this.valueForCopy);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
  },
};
</script>

<template>
  <div class="w-full h-5 ltr:-ml-1 rtl:-mr-1">
    <a
      v-if="href"
      :href="href"
      class="flex items-center gap-2 text-n-slate-11 hover:underline"
      @click="handleLinkClick"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
        :title="value"
        translate="no"
      >
        <span
          v-if="valuePrefix"
          class="italic px-1 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-300"
        >
          {{ valuePrefix }}
        </span>
        {{ value }}
      </span>
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
      <NextButton
        v-if="showCopy"
        ghost
        xs
        slate
        class="ltr:-ml-1 rtl:-mr-1"
        icon="i-lucide-clipboard"
        @click="onCopy"
      />
    </a>

    <div v-else class="flex items-center gap-2 text-n-slate-11">
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
        translate="no"
      >
        <span
          v-if="valuePrefix"
          class="italic text-xs px-1 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-300 mr-1"
        >
          {{ valuePrefix }}
        </span>
        <span v-dompurify-html="value" />
      </span>
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
    </div>
  </div>
</template>
